import { useState, useRef, useEffect } from 'react';

// Extracted from StudentProfile.js (Phase 3 of the platform-health plan:
// getting the file under ~600 lines needs pulling logic into hooks, not
// just JSX into components). Owns everything about the Materials tab:
// adding a link/file (including drag-and-drop), renaming, deleting, and
// previewing a material. Materials themselves still live on
// studentData.materials — this hook only reads/writes through
// setStudentData, same as before.
const createSafeObjectURL = (file) => {
  try {
    if (file && file instanceof File) {
      return URL.createObjectURL(file);
    }
    return null;
  } catch (error) {
    console.error('Error creating object URL:', error);
    return null;
  }
};

function todayFormatted() {
  const today = new Date();
  return `${(today.getMonth() + 1).toString().padStart(2, '0')}/${today.getDate().toString().padStart(2, '0')}/${today.getFullYear()}`;
}

export function useMaterialsManager({ studentData, setStudentData, showCustomModalConfig, setShowCustomModal }) {
  const [newMaterialLink, setNewMaterialLink] = useState('');
  const [newMaterialFile, setNewMaterialFile] = useState(null);
  const [materialType, setMaterialType] = useState('file'); // 'file' или 'link'
  const materialFileInputRef = useRef(null);
  const [materialSearchTerm, setMaterialSearchTerm] = useState('');
  const [editingMaterialId, setEditingMaterialId] = useState(null);
  const [editingMaterialName, setEditingMaterialName] = useState('');
  const [previewMaterial, setPreviewMaterial] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleAddLink = () => {
    if (newMaterialLink.trim()) {
      const url = newMaterialLink.trim();
      const name = url.length > 50 ? url.substring(0, 47) + '...' : url;
      setStudentData(prev => ({
        ...prev,
        materials: [...prev.materials, { type: 'link', name: name, value: url, dateOfUpload: todayFormatted() }]
      }));
      setNewMaterialLink('');
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setNewMaterialFile(file);
      // Автоматически добавляем файл
      const fileUrl = createSafeObjectURL(file);
      setStudentData(prev => ({
        ...prev,
        materials: [...prev.materials, {
          type: 'file',
          name: file.name,
          value: fileUrl,
          file: file,
          extension: file.name.split('.').pop().toLowerCase(),
          dateOfUpload: todayFormatted()
        }]
      }));

      // Очищаем input
      if (materialFileInputRef.current) {
        materialFileInputRef.current.value = "";
      }
      setNewMaterialFile(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      setNewMaterialFile(file);
      // Автоматически добавляем файл
      const fileUrl = createSafeObjectURL(file);
      setStudentData(prev => ({
        ...prev,
        materials: [...prev.materials, {
          type: 'file',
          name: file.name,
          value: fileUrl,
          file: file,
          extension: file.name.split('.').pop().toLowerCase(),
          dateOfUpload: todayFormatted()
        }]
      }));

      setNewMaterialFile(null);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Проверяем, что мы действительно покидаем элемент, а не переходим к дочернему
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    if (x <= rect.left || x >= rect.right || y <= rect.top || y >= rect.bottom) {
      setIsDragOver(false);
    }
  };

  const handleDeleteMaterial = (index) => {
    showCustomModalConfig({
      title: 'Delete Material',
      message: `Are you sure you want to delete "${studentData.materials[index].name}"? This action cannot be undone.`,
      type: 'warning',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      showCancel: true,
      onConfirm: () => {
        const updatedMaterials = [...studentData.materials];
        updatedMaterials.splice(index, 1);
        setStudentData(prev => ({
          ...prev,
          materials: updatedMaterials
        }));
        setShowCustomModal(false);
      }
    });
  };

  const handleEditMaterialName = (index) => {
    setEditingMaterialId(index);
    setEditingMaterialName(studentData.materials[index].name);
  };

  const handleSaveMaterialName = (index) => {
    if (editingMaterialName.trim()) {
      const material = studentData.materials[index];
      let newName = editingMaterialName.trim();

      // Для файлов автоматически добавляем расширение, если его нет
      if (material.type === 'file' && material.extension) {
        const hasExtension = newName.includes('.');
        if (!hasExtension) {
          newName = newName + '.' + material.extension;
        } else {
          // Проверяем, что расширение файла не изменилось
          const newExtension = newName.split('.').pop().toLowerCase();
          if (newExtension !== material.extension.toLowerCase()) {
            return;
          }
        }
      }

      const updatedMaterials = [...studentData.materials];
      updatedMaterials[index] = {
        ...updatedMaterials[index],
        name: newName
      };
      setStudentData(prev => ({
        ...prev,
        materials: updatedMaterials
      }));
    }
    setEditingMaterialId(null);
    setEditingMaterialName('');
  };

  const handleCancelEditMaterial = () => {
    setEditingMaterialId(null);
    setEditingMaterialName('');
  };

  const handleMaterialPreview = (material) => {
    // Для ссылок открываем сразу в новой вкладке
    if (material.type === 'link') {
      window.open(material.value, '_blank', 'noopener,noreferrer');
      return;
    }
    setPreviewMaterial(material);
  };

  const handleClosePreview = () => {
    setPreviewMaterial(null);
  };

  // Автоматическая прокрутка к модальному окну превью и блокировка прокрутки страницы
  useEffect(() => {
    if (previewMaterial) {
      // Блокируем прокрутку страницы
      document.body.style.overflow = 'hidden';

      const modalElement = document.querySelector('.preview-modal');
      if (modalElement) {
        modalElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    } else {
      // Восстанавливаем прокрутку страницы
      document.body.style.overflow = 'auto';
    }

    // Очистка при размонтировании компонента
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [previewMaterial]);

  const filteredMaterials = studentData.materials?.filter(material =>
    (material.name || '').toLowerCase().includes(materialSearchTerm.toLowerCase())
  ) || [];

  return {
    newMaterialLink, setNewMaterialLink,
    newMaterialFile,
    materialType, setMaterialType,
    materialFileInputRef,
    materialSearchTerm, setMaterialSearchTerm,
    editingMaterialId, editingMaterialName, setEditingMaterialName,
    previewMaterial,
    isDragOver,
    filteredMaterials,
    handleAddLink,
    handleFileChange,
    handleDragOver,
    handleDragEnter,
    handleDrop,
    handleDragLeave,
    handleDeleteMaterial,
    handleEditMaterialName,
    handleSaveMaterialName,
    handleCancelEditMaterial,
    handleMaterialPreview,
    handleClosePreview,
  };
}
